import { HTMLAttributes, useMemo, useRef, useState } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'

import Container from '@/components/base/container'
import CtaButton from '@/components/base/cta-button'
import Grid from '@/components/base/grid'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import { relatedProductsRevealAnimation } from '@/components/sections/animations/related-products'
import Spacer from '@/components/sections/spacer'
import { useStore } from '@/contexts/store'
import { capitalizeLetters } from '@/utils/strings'

import { cn } from '@/lib/utils'
import { WPImage } from '@/types'

type RelatedProduct = {
  title: string
  link: string
  image: Partial<WPImage>
}

type RelatedCollection = {
  title: string
  products: RelatedProduct[]
}

type RelatedProductsProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    project_name: string
    collections: RelatedCollection[]
  }>

const RelatedProducts = ({
  project_name,
  collections,
  className,
  ...props
}: RelatedProductsProps) => {
  const rootRef = useRef<HTMLDivElement>(null!)
  const imageViewportWrapRef = useRef<HTMLDivElement>(null!)
  const imageViewportRef = useRef<HTMLDivElement>(null!)
  const imageTrackRef = useRef<HTMLDivElement>(null!)
  const [activeIndex, setActiveIndex] = useState(0)
  const { strings } = useStore()

  const normalizedCollections = useMemo(() => {
    let currentIndex = 0

    return (collections || []).map((collection) => ({
      ...collection,
      products: (collection.products || []).map((product) => {
        const indexedProduct = {
          ...product,
          index: currentIndex
        }

        currentIndex += 1

        return indexedProduct
      })
    }))
  }, [collections])

  const allProducts = useMemo(
    () => normalizedCollections.flatMap((collection) => collection.products),
    [normalizedCollections]
  )

  useGSAP(
    () => {
      if (
        !rootRef.current ||
        !imageTrackRef.current ||
        !imageViewportRef.current
      )
        return

      const rows = gsap.utils.toArray<HTMLElement>(
        '[data-related-product-row]',
        rootRef.current
      )

      const activeRow =
        rows.find(
          (row) => Number(row.dataset.relatedProductIndex) === activeIndex
        ) || rows[0]

      const firstImage = imageTrackRef.current.querySelector(
        '[data-related-product-image]'
      ) as HTMLElement | null

      if (!firstImage) return

      const imageHeight = imageViewportRef.current.offsetHeight

      gsap.to(imageTrackRef.current, {
        y: -activeIndex * imageHeight,
        duration: 0.45,
        ease: 'power2.out'
      })

      if (activeRow && imageViewportWrapRef.current) {
        const wrapRect = imageViewportWrapRef.current.getBoundingClientRect()
        const rowRect = activeRow.getBoundingClientRect()
        const viewportHeight = imageViewportRef.current.offsetHeight

        const y =
          rowRect.top + rowRect.height / 2 - wrapRect.top - viewportHeight / 2

        gsap.to(imageViewportRef.current, {
          y,
          duration: 0.45,
          ease: 'power2.out'
        })
      }
    },
    {
      dependencies: [activeIndex, allProducts.length],
      scope: rootRef
    }
  )

  return (
    <Motion animation={relatedProductsRevealAnimation}>
      {(motionRef) => (
        <div ref={motionRef}>
          <Section ref={rootRef} className={cn(className)} {...props}>
            <Spacer variant="xl" />
            <Container>
              <Grid className="gap-y-spacing-xl">
                <div className="col-span-2 md:col-span-6 lg:col-span-6">
                  <Typography
                    variant="h3"
                    component="h2"
                    className="text-h3 md:text-d-h2"
                    data-motion-title
                  >
                    {strings?.labels?.related_products_prefix ||
                      'I Prodotti in'}
                    <br />
                    {project_name || ''}
                  </Typography>
                </div>

                <div className="col-span-2 md:col-span-6 lg:col-span-3 lg:col-start-10">
                  <div className="relative flex gap-spacing-sm">
                    <div
                      ref={imageViewportWrapRef}
                      className="absolute left-0 top-0 hidden w-[63%] translate-x-[calc(-100%-3rem)] lg:block"
                    >
                      <div
                        ref={imageViewportRef}
                        className="absolute left-0 top-0 aspect-[4/3] w-full overflow-hidden"
                      >
                        <div ref={imageTrackRef} className="flex flex-col">
                          {allProducts.map((product, index) => (
                            <div
                              key={`related-image-${product.title}-${index}`}
                              data-related-product-image
                              className="relative aspect-[4/3] w-full shrink-0"
                            >
                              <img
                                src={product.image?.url}
                                alt={product.image?.alt || product.title}
                                className="absolute inset-0 h-full w-full object-cover"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-1 flex-col gap-spacing-lg md:gap-spacing-xl">
                      {normalizedCollections.map(
                        (collection, collectionIndex) => (
                          <div
                            key={`related-collection-${collection.title}-${collectionIndex}`}
                            className=""
                          >
                            <Typography
                              variant="h6"
                              className="mb-spacing-sm md:mb-spacing-md"
                            >
                              {collection.title}
                            </Typography>

                            <div className="flex flex-col">
                              {collection.products.map((product) => (
                                <div
                                  key={`related-product-${collection.title}-${product.title}`}
                                  data-related-product-row
                                  data-related-product-index={product.index}
                                  className="flex items-center border-b border-border-secondary-light py-spacing-2xs dark:border-border-secondary-dark"
                                  onMouseEnter={() =>
                                    setActiveIndex(product.index)
                                  }
                                  onFocus={() => setActiveIndex(product.index)}
                                >
                                  <div className="mr-spacing-2xs h-[40px] w-[60px] shrink-0 overflow-hidden lg:hidden">
                                    <img
                                      src={product.image?.url}
                                      alt={product.image?.alt || product.title}
                                      className="h-full w-full object-cover"
                                    />
                                  </div>

                                  <CtaButton
                                    href={product.link || '#'}
                                    variant="link"
                                    typography="regular"
                                    className="w-full"
                                  >
                                    {capitalizeLetters(product.title)}
                                  </CtaButton>
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </Grid>
            </Container>
            <Spacer variant="xl" />
          </Section>
        </div>
      )}
    </Motion>
  )
}

export default RelatedProducts
